import { actAsErrorMessage } from '@/utils/simulationScopes'
import axios from "@axios"
import moment from 'moment-timezone'
import lodash from "underscore"
import localforage   from "localforage"
import {useTitle} from "@vueuse/core"

export let stateAbbreviations
stateAbbreviations = {
  "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR",
  "California": "CA", "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE",
  "Florida": "FL", "Georgia": "GA", "Hawaii": "HI", "Idaho": "ID",
  "Illinois": "IL", "Indiana": "IN", "Iowa": "IA", "Kansas": "KS",
  "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
  "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS",
  "Missouri": "MO", "Montana": "MT", "Nebraska": "NE", "Nevada": "NV",
  "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY",
  "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK",
  "Oregon": "OR", "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX", "Utah": "UT",
  "Vermont": "VT", "Virginia": "VA", "Washington": "WA", "West Virginia": "WV",
  "Wisconsin": "WI", "Wyoming": "WY"
}

//takes a comma separated list of things and returns a string with the last item separated by '&'
export function addAndToString(theString) {
  if (!theString) return ''
  let arr = theString.split(',')
  if (arr.length === 1) return arr[0]
  let lastItem = arr.pop()
  
  return arr.join(', ') + ' & ' + lastItem
}

export function addOrToString(theString) {
  if (!theString) return ''
  let arr = theString.split(',')
  
  return arr.join(' or ')
}

/**
 * RM's ONE phone format: "(312) 925-0238" (Kapil, 2026-09-23: "I always prefer
 * this format… we should have that standard across the app"). A US number
 * reads the same with or without its country code — this used to return
 * "+1 (312) 925-0238" for one and "(312) 925-0238" for the other. Anything that
 * is not a US number returns null.
 */
export function formatPhoneNumber(phoneNumberString) {
  const cleaned = String(phoneNumberString == null ? '' : phoneNumberString).replace(/\D/g, '')
  const match = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/)
  if (match) return `(${match[1]}) ${match[2]}-${match[3]}`

  return null
}

/**
 * A phone number as RM shows it, anywhere: the one format for a US number, and
 * a number that is not a US number exactly as it was given. Every screen that
 * shows a phone goes through this, so none can drift from the standard.
 */
export function displayPhone(value) {
  if (value == null || value === '') return ''

  return formatPhoneNumber(value) || String(value)
}

export const numberWithCommas = lodash.memoize(function (x) {
  if (x === null || x === undefined) {
    return ''
  } else {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }
})

export function formatCurrency(value) {
  if (!value || value === 0) return '-'
  const absValue = Math.abs(value)

  let formattedValue
  if (absValue >= 1e6) {
    formattedValue = (value / 1e6).toPrecision(3) + 'M'
  } else if (absValue >= 1e3) {
    formattedValue = (value / 1e3).toPrecision(3) + 'K'
  } else {
    formattedValue = value.toPrecision(3)
  }

  // Remove unnecessary trailing zeros and the decimal point
  formattedValue = formattedValue.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1')

  return `$${formattedValue}`
}

export function generateGeneralReservationUrlWithQueryString(restaurant, reservation) {
  let date, preferredTime24h, partySize, dateTime, fourDigitTime
  if (reservation) {
    date = reservation.date
    preferredTime24h = moment(reservation.preferredTime, "h:mma").format("k:mm")
    partySize = reservation.partySize
    dateTime = moment(reservation.date + ' ' + reservation.preferredTime, 'YYYY-MM-DD hh:mm:a').format('YYYY-MM-DDTkk:mm')
    fourDigitTime = moment(reservation.preferredTime, "h:mma").format('kkmm')
  }

  //else just for previewing
  else {
    date = moment().add(1, 'day').format("YYYY-MM-DD")
    preferredTime24h = "19:00"
    partySize = 2
    dateTime = moment().add(1, 'day').format('YYYY-MM-DDT19:00')
    fourDigitTime = "1900"
  }

  if (restaurant.generalReservationUrl.toLowerCase().includes('opentable.com')) {
    return `${restaurant.generalReservationUrl}?datetime=${dateTime}&covers=${partySize}&searchdatetime=${dateTime}&partysize=${partySize}&lang=en`
  }
  else if (restaurant.generalReservationUrl.toLowerCase().includes('tock.com')) {
    return `${restaurant.generalReservationUrl}/search?date=${date}&size=${partySize}&time=${preferredTime24h}`
  }
  else if (restaurant.generalReservationUrl.toLowerCase().includes('resy.com')) {
    return `${restaurant.generalReservationUrl}?date=${date}&seats=${partySize}&time=${fourDigitTime}`
  }
  else if (restaurant.generalReservationUrl.toLowerCase().includes('sevenrooms.com')) {
    return `${restaurant.generalReservationUrl}?default_date=${date}&default_party_size=${partySize}&default_time=${preferredTime24h}`
  }
  else if (restaurant.generalReservationUrl.toLowerCase().includes('yelp.com')) {
    return `${restaurant.generalReservationUrl}?covers=${partySize}&date=${date}&time=${fourDigitTime}`
  }
  
  return restaurant.generalReservationUrl
}

export function extractDomain(url) {
  if (!url) return null
  const hostname = new URL(url).hostname
  const parts = hostname.split('.')

  // This will cover most cases but may need adjustment for domains with longer TLDs
  if (parts.length > 2) {
    return parts.slice(-2).join('.')
  }

  return hostname
}

const maxNumberOfErrorsToSend = 10
let numberOfErrorsSent = 0
const maxNumberOfWarningsToSend = 5
let numberOfWarningsSent = 0

/**
 * Report a JavaScript error to the backend (for Slack notification)
 *
 * @param {string} type - Error type/location identifier
 * @param {string} msg - Error message
 * @param {string} url - URL where error occurred
 * @param {number} lineNo - Line number
 * @param {number} columnNo - Column number
 * @param {Error} error - The error object
 */
export function reportError(type, msg, url, lineNo, columnNo, error) {
  // Rate limit error reporting
  if (numberOfErrorsSent >= maxNumberOfErrorsToSend) {
    console.warn('[Error Reporting] Rate limit reached, error not sent:', msg)
    
    return
  }

  console.error(`[Error] ${type}:`, msg, { url, lineNo, columnNo, error })

  // Send to backend asynchronously
  axios.post(
    `/reporting/js-client-error`,
    {
      type: type,
      message: msg,
      stack: error?.stack || '',
      info: JSON.stringify({ url, lineNo, columnNo, href: window.location.href }),
      userAgent: navigator.userAgent
    },
    { params: {} }
  ).catch(reportingError => {
    console.error('[Error Reporting] Failed to send error:', reportingError.message)
  })

  numberOfErrorsSent++
}

/**
 * Vue error handler - reports Vue-specific errors to backend
 *
 * @param {Error} err - The error object
 * @param {Object} instance - Vue component instance
 * @param {string} info - Vue-specific error info
 */
export function reportErrorVue(err, instance, info) {
  // Rate limit error reporting
  if (numberOfErrorsSent >= maxNumberOfErrorsToSend) {
    console.warn('[Vue Error] Rate limit reached, error not sent:', err?.message)
    
    return
  }

  const componentName = instance?.$options?.name || instance?.$options?.__name || 'Unknown'
  console.error(`[Vue Error] ${componentName}:`, err, { info })

  // Send to backend asynchronously
  axios.post(
    `/reporting/js-client-error`,
    {
      type: `Vue:${componentName}`,
      message: err?.message || 'Unknown Vue error',
      stack: err?.stack || '',
      info: info,
      userAgent: navigator.userAgent
    },
    { params: {} }
  ).then(() => {
    // Handle known recoverable errors
    if (err?.message?.includes('Expected mapDiv')) {
      console.warn('[Vue Error] Reloading page due to Google Maps error')
      location.reload()
    }
  }).catch(reportingError => {
    console.error('[Error Reporting] Failed to send Vue error:', reportingError.message)
  })

  numberOfErrorsSent++
}

/**
 * Vue warning handler - reports Vue warnings to backend (in production)
 *
 * @param {string} msg - Warning message
 * @param {Object} instance - Vue component instance
 * @param {string} trace - Component trace
 */
export function reportWarningVue(msg, instance, trace) {
  // Rate limit warning reporting
  if (numberOfWarningsSent >= maxNumberOfWarningsToSend) {
    return
  }

  const componentName = instance?.$options?.name || instance?.$options?.__name || 'Unknown'
  console.warn(`[Vue Warning] ${componentName}:`, msg)

  // Send to backend asynchronously
  axios.post(
    `/reporting/js-client-warning`,
    {
      type: `Vue:${componentName}`,
      message: msg,
      stack: trace || '',
      userAgent: navigator.userAgent
    },
    { params: {} }
  ).catch(reportingError => {
    console.error('[Warning Reporting] Failed to send Vue warning:', reportingError.message)
  })

  numberOfWarningsSent++
}

export function buildProfileUrl(platformName, profileId) {
  
  if (platformName.toLowerCase() === 'instagram' && !profileId.startsWith("https://")) {
    return `https://www.instagram.com/${profileId}`
  }
  else if (platformName.toLowerCase() === 'twitter' && !profileId.startsWith("https://")) {
    return `https://www.twitter.com/${profileId}`
  }
  else if (platformName.toLowerCase() === 'linkedin' && !profileId.startsWith("https://")) {
    return `https://www.linkedin.com/in/${profileId}`
  }
  else if (platformName.toLowerCase() === 'tiktok' && !profileId.startsWith("https://")) {
    return `https://www.tiktok.com/@${profileId}`
  }

  return profileId
}

// RM-150: callers can override the post-login redirect target by passing
// `goTo` (or by setting `localStorage.simulationGoTo` before invocation).
// Used by the "Edit Profile" action in the request flow to deep-link the
// admin straight into the member's profile editor rather than dumping
// them on the home page.
// Volatile localForage caches that are user-specific and must not bleed across
// simulation level changes.
const SIMULATION_CACHE_KEYS = [
  'userGroups',
  'restaurantUserGroups',
  'myReservations',
  'completeReservations',
  'inReviewReservations',
  'justBookedReservations',
]

// RM-150: simulation supports NESTING (a platform admin can simulate a group
// admin, who can then "Manage Account" to simulate one of their own members).
// The exit chain therefore has to be a stack, not a single saved token —
// otherwise a deeper simulation overwrites the parent's restore token and the
// admin can only exit one level. Each stack entry captures the jwt + href to
// return to when THAT level is exited.
function readSimulationStack() {
  try {
    return JSON.parse(localStorage.getItem('simulationStack') || '[]')
  } catch (_) {
    return []
  }
}

function writeSimulationStack(stack) {
  if (stack.length === 0) {
    localStorage.removeItem('simulationStack')
    localStorage.removeItem('adminJwt')
    localStorage.removeItem('simulationHref')

    return
  }
  localStorage.setItem('simulationStack', JSON.stringify(stack))

  // Keep the legacy single keys in sync with the top of the stack so any other
  // reader / logout handler that still inspects them behaves correctly.
  const top = stack[stack.length - 1]
  if (top?.jwt != null) localStorage.setItem('adminJwt', top.jwt)
  if (top?.href != null) localStorage.setItem('simulationHref', top.href)
}

async function clearSimulationCaches() {
  for (const key of SIMULATION_CACHE_KEYS) {
    try {
      await localforage.removeItem(key)
    } catch (_) {
      // best-effort
    }
  }
}

// RM-150: callers can override the post-login redirect target by passing
// `goTo` (or by setting `localStorage.simulationGoTo` before invocation).
// Used by the "Edit Profile" action in the request flow to deep-link the
// admin straight into the member's profile editor rather than dumping
// them on the home page.
export function simulation(email, options = {}) {
  const stack = readSimulationStack()
  const isFirstLevel = stack.length === 0

  // Push the state to return to when THIS simulation level is exited. We save
  // the current jwt (the initiator's session) + href + scope. On nested
  // simulation this preserves the parent level instead of clobbering it.
  // `scope` is the parent level's simulationScope (null at the top), restored on
  // exit so the UI knows what kind of session it's back in (RM-159).
  stack.push({
    jwt: localStorage.getItem('jwt'),
    href: location.href,
    scope: localStorage.getItem('simulationScope') || null,
  })
  writeSimulationStack(stack)

  const goTo = options.goTo || localStorage.getItem('simulationGoTo') || '/'

  axios.post('/auth/login/simulation', {
    email: email,
    password: email
  }, {
    params: {
    }
  }).then(async r => {
    const { userData, userAbilities, jwt, simulation, simulationScope } = r.data
    localStorage.setItem('userAbilities', JSON.stringify(userAbilities))

    //ability.update(userAbilities)
    localStorage.setItem('userData', JSON.stringify(userData))
    localStorage.setItem('accessToken', JSON.stringify(jwt))
    localStorage.setItem('jwt', JSON.stringify(jwt))
    localStorage.setItem('simulation', JSON.stringify(simulation))

    // RM-159: persist the relationship that authorized THIS simulation level so
    // the UI can adapt (e.g. a spouse simulating their partner still sees the
    // Spouse/Partner Account tile, which is otherwise hidden during admin
    // simulation). Stored raw (plain string) for a simple equality check.
    if (simulationScope) {
      localStorage.setItem('simulationScope', simulationScope)
    } else {
      localStorage.removeItem('simulationScope')
    }

    if (isFirstLevel) {
      // Save the real (pre-simulation) caches under *Saved keys so they can be
      // restored on the final exit. Only on the first level — a deeper level
      // must not overwrite the original admin's saved caches.
      await renameLocalForageKey("userGroups", "userGroupsSaved")

      // await renameLocalForageKey("users", "usersSaved")
      await renameLocalForageKey("restaurantUserGroups", "restaurantUserGroupsSaved")
      await renameLocalForageKey("myReservations", "myReservationsSaved")
      await renameLocalForageKey("completeReservations", "completeReservationsSaved")
      await renameLocalForageKey("inReviewReservations", "inReviewReservationsSaved")
      await renameLocalForageKey("justBookedReservations", "justBookedReservationsSaved")
    } else {
      // Deeper level: drop the parent simulated user's volatile caches so the
      // newly simulated user doesn't read stale cross-user data.
      await clearSimulationCaches()
    }

    localStorage.removeItem('simulationGoTo')
    location.href = goTo

    return null
  }).catch(e => {
    // Roll back the stack push so a failed simulation doesn't leave a phantom
    // level that breaks the exit chain.
    const rollback = readSimulationStack()
    rollback.pop()
    writeSimulationStack(rollback)
    console.error('Simulation login failed:', e?.response?.data || e)
    // RM-337 item 5: say why, rather than a tile that does nothing — in the
    // app's snackbar (D21, 9/30), not a browser alert.
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('rm-toast', { detail: { message: actAsErrorMessage(e) } }))
  })
}

export function exitSimulation() {
  const stack = readSimulationStack()

  // Pop the level being exited. Its entry holds the jwt/href to return to.
  // Fallback to the legacy single keys for sessions that began before the stack
  // existed (e.g. mid-simulation across a deploy).
  const entry = stack.pop() || {
    jwt: localStorage.getItem('adminJwt'),
    href: localStorage.getItem('simulationHref'),
    scope: null,
  }
  const isLastLevel = stack.length === 0

  // Send the saved jwt as a fallback in case the httpOnly cookie was lost or
  // overwritten (e.g. browser cleared cookies, iOS WebKit). It was stored as
  // localStorage.getItem('jwt') (i.e. JSON.stringify(token)), so parse it.
  let parsedAdminJwt = null
  try {
    if (entry.jwt) parsedAdminJwt = JSON.parse(entry.jwt)
  } catch (e) {
    // If JSON parse fails, use the raw value (could be a bare token string)
    parsedAdminJwt = entry.jwt
  }

  const savedHref = entry.href || '/'

  axios.post('/auth/login/exitsimulation', {
    adminJwt: parsedAdminJwt,
  }, {
    params: {
    }
  }).then(async r => {
    const { userData, userAbilities, jwt } = r.data
    localStorage.setItem('userAbilities', JSON.stringify(userAbilities))

    //ability.update(userAbilities)
    localStorage.setItem('userData', JSON.stringify(userData))
    localStorage.setItem('accessToken', JSON.stringify(jwt))
    localStorage.setItem('jwt', JSON.stringify(jwt))

    // Persist the popped stack (also re-syncs the legacy keys to the new top).
    writeSimulationStack(stack)

    if (isLastLevel) {
      // Back to the original (non-simulated) admin — tear down simulation state
      // and restore the admin's saved caches.
      localStorage.removeItem('simulation')
      localStorage.removeItem('simulationStack')
      localStorage.removeItem('adminJwt')
      localStorage.removeItem('simulationHref')
      localStorage.removeItem('simulationScope')

      await renameLocalForageKey("userGroupsSaved", "userGroups")

      // await renameLocalForageKey("usersSaved", "users")
      await renameLocalForageKey("restaurantUserGroupsSaved", "restaurantUserGroups")
      await renameLocalForageKey("myReservationsSaved", "myReservations")
      await renameLocalForageKey("completeReservationsSaved", "completeReservations")
      await renameLocalForageKey("inReviewReservationsSaved", "inReviewReservations")
      await renameLocalForageKey("justBookedReservationsSaved", "justBookedReservations")
    } else {
      // Still simulating a parent level — keep the `simulation` flag so the Exit
      // button stays visible, restore the parent level's scope, and drop the
      // exited user's volatile caches so the parent reloads fresh data.
      if (entry.scope) {
        localStorage.setItem('simulationScope', entry.scope)
      } else {
        localStorage.removeItem('simulationScope')
      }
      await clearSimulationCaches()
    }

    location.href = savedHref

    return null
  }).catch(e => {
    console.error('Exit simulation failed:', e?.response?.status, e?.response?.data || e)

    // Fallback: clear all simulation state and redirect to login so the
    // admin can recover instead of being stuck in a broken state.
    localStorage.removeItem('simulation')
    localStorage.removeItem('simulationStack')
    localStorage.removeItem('simulationHref')
    localStorage.removeItem('simulationScope')
    localStorage.removeItem('adminJwt')
    localStorage.removeItem('userData')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('jwt')
    location.href = '/auth/logout?loc=5'
  })
}

export function getFullUrl(url) {
  if (!url) return url // return if the URL is null or undefined
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url // the URL is already complete
  } else {
    return 'https://' + url // prepend the protocol
  }
}

export function encodeUUID(uuid) {
  const hex = uuid.replace(/-/g, '')
  const buffer = new Uint8Array(hex.length / 2)
  for (let i = 0; i < buffer.length; i++) {
    buffer[i] = parseInt(hex.substr(i * 2, 2), 16)
  }
  
  return btoa(String.fromCharCode.apply(null, buffer))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function decodeUUID(encodedUuid) {
  const base64 = encodedUuid.replace(/-/g, '+').replace(/_/g, '/')
    + '='.repeat((4 - encodedUuid.length % 4) % 4)
  const binStr = atob(base64)
  const buffer = new Uint8Array(binStr.length)
  for (let i = 0; i < binStr.length; i++) {
    buffer[i] = binStr.charCodeAt(i)
  }
  const hex = Array.from(buffer).map(b => b.toString(16).padStart(2, '0')).join('')
  
  return `${hex.substr(0, 8)}-${hex.substr(8, 4)}-${hex.substr(12, 4)}-${hex.substr(16, 4)}-${hex.substr(20)}`
}

export function restaurantUrlSegment(marketName, restaurantName) {
  let marketString = marketName ? marketName.toLowerCase() + '-' : ''
  
  return cleanForUrl(`${marketString}${restaurantName.toLowerCase()}`)
}

export function cleanForUrl(text) {
  return text.toLowerCase()
    .replace(/ /g, '-')
    .replace(/, /g, '-') // Replace comma-space with hyphens
    .replace(/ /g, '-') // Replace spaces with hyphens
    .replace(/'/g, '')  //get rid of any single-quotes in profileId
    //replace any fancy letters with normal ones
    .replace(/á/g, 'a')
    .replace(/é/g, 'e')
    .replace(/í/g, 'i')
    .replace(/ó/g, 'o')
    .replace(/ú/g, 'u')
    .replace(/ñ/g, 'n')
    .replace(/ü/g, 'u')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9\-]/g, '')

    //consolidate multiple dashes in a row to single dash
    .replace(/-+/g, '-')
}

export async function renameLocalForageKey(oldKey, newKey) {
  try {
    // Step 1: Retrieve the item using the old key
    const item = await localforage.getItem(oldKey)
    if (item === null) {
      console.log('Item does not exist.')
      
      return
    }

    // Step 2: Save the item with the new key
    await localforage.setItem(newKey, item)

    // Step 3: Delete the item with the old key
    await localforage.removeItem(oldKey)

    console.log(`Item with key '${oldKey}' has been renamed to '${newKey}'.`)
  } catch (error) {
    console.error('Error renaming the key:', error)
  }
}

export async function setMetaTag(name, content) {
  let head = document.getElementsByTagName('head')[0]
  let metaTag = document.querySelector(`meta[name="${name}"]`)
  if (!metaTag) {
    metaTag = document.createElement('meta')
    metaTag.name = name
    head.appendChild(metaTag)
  }
  metaTag.content = content
}

export async function setMetaData(title, description, imageUrl, link) {
  useTitle(title)
  await setMetaTag('description', description)
  await setMetaTag('image', imageUrl)
  await setMetaTag('og:title', title)
  await setMetaTag('og:description', description)
  await setMetaTag('og:image', imageUrl)
  await setMetaTag('og:image:url', imageUrl)
  await setMetaTag('og:image:secure_url', imageUrl)

  // setMetaTag('og:image:type', 'image/jpeg');
  // setMetaTag('og:image:width', '1200');
  // setMetaTag('og:image:height', '630');
  await setMetaTag('og:image:alt', description)
  await setMetaTag('og:site_name', 'reserverm.com')
  await setMetaTag('og:url', link)
  await setMetaTag('og:type', 'website')
  await setMetaTag('twitter:card', 'summary')
  await setMetaTag('twitter:title', title)
  await setMetaTag('twitter:description', description)
  await setMetaTag('twitter:image', imageUrl)

  // document.querySelector('meta[name="fb:app_id"]').setAttribute("content", "156994501850424");

  //we only want library and template content to be indexed
  // if (shouldBeIndexed) {
  //     document.querySelector('meta[name="robots"]').setAttribute("content", "all");
  // } else {
  //     document.querySelector('meta[name="robots"]').setAttribute("content", "noindex, nofollow");
  // }
}

export function bookingTimingText(reservation) {
  //time difference in hours between requestedAt and date+preferredTim
  let requestedAtMoment = moment.tz(reservation.submittedAt, reservation.identifier)
  let acceptedAtMoment = moment.tz(reservation.acceptedAt, reservation.identifier)
  let preferredMoment = moment.tz(reservation.date + ' ' + reservation.confirmedTime, 'YYYY-MM-DD h:mma', reservation.identifier)
  let preferredToRequestedAtDiff = preferredMoment.diff(requestedAtMoment, 'hours')
  let acceptedAtToRequestedAtDiff = acceptedAtMoment.diff(requestedAtMoment, 'minutes')
  let securedFor = "for months out"
  if (preferredToRequestedAtDiff < 24) {
    securedFor = "same day"
  } else if (preferredToRequestedAtDiff < 48) {
    securedFor = "for next day"
  } else if (preferredToRequestedAtDiff < 8 * 24) {
    securedFor = "same week"
  } else if (preferredToRequestedAtDiff < 14 * 24) {
    securedFor = "for next week"
  } else if (preferredToRequestedAtDiff < 31 * 24) {
    securedFor = "for weeks out"
  }

  let securedIn = "days"
  if (acceptedAtToRequestedAtDiff < 60) {
    securedIn = "minutes"
  } else if (acceptedAtToRequestedAtDiff < 24 * 60) {
    securedIn = "hours"
  }

  return { line1: `secured ${securedFor}`, line2: `accepted in ${securedIn}` }
}

export function newBookingTimingText(reservation) {
  //time difference in hours between requestedAt and date+preferredTim
  let requestedAtMoment = moment.tz(reservation.submittedAt, reservation.identifier)
  let acceptedAtMoment = moment.tz(reservation.acceptedAt, reservation.identifier)
  let preferredMoment = moment.tz(reservation.date + ' ' + reservation.confirmedTime, 'YYYY-MM-DD h:mma', reservation.identifier)
  let preferredToRequestedAtDiff = preferredMoment.diff(requestedAtMoment, 'hours')
  let acceptedAtToRequestedAtDiff = acceptedAtMoment.diff(requestedAtMoment, 'minutes')
  let returnString = "for months out"
  if (preferredToRequestedAtDiff < 24) {
    returnString = "for same day"
  } else if (preferredToRequestedAtDiff < 48) {
    returnString = "for next day"
  } else if (preferredToRequestedAtDiff < 8 * 24) {
    returnString = "for same week"
  } else if (preferredToRequestedAtDiff < 14 * 24) {
    returnString = "for next week"
  } else if (preferredToRequestedAtDiff < 31 * 24) {
    returnString = "for weeks out"
  }

  let securedIn = "days"
  if (acceptedAtToRequestedAtDiff < 60) {
    securedIn = "mins"
  } else if (acceptedAtToRequestedAtDiff < 24 * 60) {
    securedIn = "hours"
  }
  returnString = returnString + " in " + securedIn

  return returnString
}

export function timingText(reservation) {
  let requestedAtMoment = moment.tz(reservation.submittedAt, reservation.identifier)
  let preferredMoment = moment.tz(reservation.date + ' ' + reservation.confirmedTime, 'YYYY-MM-DD h:mma', reservation.identifier)
  let preferredToRequestedAtDiff = preferredMoment.diff(requestedAtMoment, 'hours')
  let returnString = 'months out'
  if (preferredToRequestedAtDiff < 24) {
    returnString = 'same day'
  } else if (preferredToRequestedAtDiff < 48) {
    returnString = 'next day'
  } else if (preferredToRequestedAtDiff < 8 * 24) {
    returnString = 'same week'
  } else if (preferredToRequestedAtDiff < 14 * 24) {
    returnString = 'next week'
  } else if (preferredToRequestedAtDiff < 31 * 24) {
    returnString = 'weeks out'
  }

  return returnString
}
